const STORAGE_KEY = 'joinsaw_daily_completed';
const SHARED_COMPLETED_KEY = 'joinsaw_shared_completed';
const LEVEL_COMPLETED_KEY = 'joinsaw_level_completed';
const LEVEL_THUMB_PREFIX = 'joinsaw_level_thumb_';
const CURRENT_PUZZLE_KEY = 'joinsaw_current_puzzle';

export interface CurrentPuzzle {
  type: 'daily' | 'level' | 'shared' | 'custom';
  id: string;
}

export class DailyPuzzleManager {
  /**
   * Get all completed daily puzzle IDs
   */
  static getCompletedPuzzles(): string[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      let result = stored ? JSON.parse(stored) : [];

      // No normalization needed - all IDs use hyphens now

      console.log('DailyPuzzleManager.getCompletedPuzzles() - raw:', stored, 'parsed:', result);
      return result;
    } catch (error) {
      console.error('Error reading completed puzzles:', error);
      return [];
    }
  }

  /**
   * Check if a daily puzzle has been completed
   */
  static isPuzzleCompleted(puzzleId: string): boolean {
    const completed = this.getCompletedPuzzles();
    return completed.includes(puzzleId);
  }

  /**
   * Mark a daily puzzle as completed
   */
  static markPuzzleCompleted(puzzleId: string): void {
    try {
      const completed = this.getCompletedPuzzles();
      if (!completed.includes(puzzleId)) {
        completed.push(puzzleId);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(completed));
      }
    } catch (error) {
      console.error('Error saving completed puzzle:', error);
    }
  }

  /**
   * Get the current puzzle being played
   */
  static getCurrentPuzzle(): CurrentPuzzle | null {
    try {
      const stored = localStorage.getItem(CURRENT_PUZZLE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error reading current puzzle:', error);
      return null;
    }
  }

  /**
   * Set the current puzzle being played
   */
  static setCurrentPuzzle(puzzle: CurrentPuzzle): void {
    try {
      localStorage.setItem(CURRENT_PUZZLE_KEY, JSON.stringify(puzzle));
    } catch (error) {
      console.error('Error saving current puzzle:', error);
    }
  }

  /**
   * Clear the current puzzle
   */
  static clearCurrentPuzzle(): void {
    try {
      localStorage.removeItem(CURRENT_PUZZLE_KEY);
    } catch (error) {
      console.error('Error clearing current puzzle:', error);
    }
  }

  /**
   * Check if a date is in the past or today (available to play)
   */
  static isDateAvailable(dateString: string): boolean {
    const puzzleDate = new Date(dateString);
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    return puzzleDate <= today;
  }

  /**
   * Get completion count for November 2025
   */
  static getMonthCompletionCount(year: number = 2025, month: number = 11): number {
    const completed = this.getCompletedPuzzles();
    const prefix = `daily_${year}_${String(month).padStart(2, '0')}_`;
    return completed.filter(id => id.startsWith(prefix)).length;
  }

  /**
   * Get all completed shared puzzle IDs
   */
  static getCompletedSharedPuzzles(): string[] {
    try {
      const stored = localStorage.getItem(SHARED_COMPLETED_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading completed shared puzzles:', error);
      return [];
    }
  }

  /**
   * Check if a shared puzzle has been completed
   */
  static isSharedPuzzleCompleted(puzzleId: string): boolean {
    const completed = this.getCompletedSharedPuzzles();
    return completed.includes(puzzleId);
  }

  /**
   * Mark a shared puzzle as completed
   */
  static markSharedPuzzleCompleted(puzzleId: string): void {
    try {
      const completed = this.getCompletedSharedPuzzles();
      if (!completed.includes(puzzleId)) {
        completed.push(puzzleId);
        localStorage.setItem(SHARED_COMPLETED_KEY, JSON.stringify(completed));
      }
    } catch (error) {
      console.error('Error saving completed shared puzzle:', error);
    }
  }

  /**
   * Get all completed level puzzle IDs
   */
  static getCompletedLevels(): string[] {
    try {
      const stored = localStorage.getItem(LEVEL_COMPLETED_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading completed levels:', error);
      return [];
    }
  }

  /**
   * Check if a level puzzle has been completed
   */
  static isLevelCompleted(puzzleId: string): boolean {
    return this.getCompletedLevels().includes(puzzleId);
  }

  /**
   * Mark a level puzzle as completed
   */
  static markLevelCompleted(puzzleId: string): void {
    try {
      const completed = this.getCompletedLevels();
      if (!completed.includes(puzzleId)) {
        completed.push(puzzleId);
        localStorage.setItem(LEVEL_COMPLETED_KEY, JSON.stringify(completed));
      }
    } catch (error) {
      console.error('Error saving completed level:', error);
    }
  }

  static saveLevelThumbnail(levelId: string, dataUrl: string): void {
    try {
      localStorage.setItem(LEVEL_THUMB_PREFIX + levelId, dataUrl);
    } catch (error) {
      console.error('Error saving level thumbnail:', error);
    }
  }

  static getLevelThumbnail(levelId: string): string | null {
    try {
      return localStorage.getItem(LEVEL_THUMB_PREFIX + levelId);
    } catch {
      return null;
    }
  }
}
